import { useEffect, useState } from 'react';
import { Link, useRevalidator, useSearchParams } from 'react-router';

import { Badge, Button, Card, Icon, IconButton } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { rotuloDaDistribuicao } from '@/data/cadastros';
import { PROVEDORES_DE_CANAL, estadoDoCanal, indisponibilidadeDoProvedor, provedorDeCanal } from '@/data/integracoes';
import { dados } from '@/data/porta';
import { AVISOS_DA_VOLTA_DA_META } from '@/data/anuncios';
import type { CargaDeAtendimentoECrm } from '@/data/cadastros';
import { CartaoDaMeta } from '../marketing/ConexaoDaMeta';
import type { CanalDeIntegracao, CargaDoPainelDeAnuncios, DadosDoCanal, PareamentoDoCanal } from '@/data/tipos';
import { PareamentoModal } from './PareamentoModal';

const NOVO: DadosDoCanal = { nome: '', tipo: 'whatsapp', provedor: 'uazapi', ativo: true };

/**
 * As integrações da clínica: quais canais existem e quais têm credencial.
 *
 * **O formulário de credencial nasce vazio, inclusive num canal já conectado.**
 * O segredo entra por uma função do banco, é cifrado no Vault e não volta —
 * nenhuma consulta do aplicativo consegue lê-lo. Preencher os campos de volta
 * exigiria mostrar um segredo, e mostrar é o que este desenho existe para
 * impedir. O que a tela sabe é que existe credencial e quando ela mudou.
 */
export function IntegracoesDaClinica({ canais, pareamentoDisponivel = false, atendimento, anuncios = null }: {
    canais: CanalDeIntegracao[];
    /** A plataforma ligou um servidor de WhatsApp? Sem isso, não há QR. */
    pareamentoDisponivel?: boolean;
    /** Filas e equipe, para o destino da conversa nova do canal (0161). */
    atendimento?: CargaDeAtendimentoECrm;
    /** A conta da Meta Ads (0162). Nulo sem permissão de marketing. */
    anuncios?: CargaDoPainelDeAnuncios | null;
}) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [params, setParams] = useSearchParams();
    const voltaDaMeta = params.get('meta-ads');

    /* A volta do login da Meta: diz o que aconteceu uma vez e limpa o endereço. */
    useEffect(() => {
        if (!voltaDaMeta) return;
        const dito = AVISOS_DA_VOLTA_DA_META[voltaDaMeta];
        if (dito && voltaDaMeta !== 'escolher') aviso(dito);
        if (voltaDaMeta !== 'escolher') {
            setParams((p) => { const n = new URLSearchParams(p); n.delete('meta-ads'); return n; }, { replace: true });
        }
    }, [voltaDaMeta, aviso, setParams]);

    const [emEdicao, setEmEdicao] = useState<CanalDeIntegracao | 'novo' | null>(null);
    const [form, setForm] = useState<DadosDoCanal>(NOVO);
    const [conectando, setConectando] = useState<CanalDeIntegracao | null>(null);
    const [credenciais, setCredenciais] = useState<Record<string, string>>({});
    const canalSujo = useAlteracaoPendente(form, emEdicao);
    const credencialSuja = useAlteracaoPendente(credenciais, conectando);
    const [confirmar, confirmacao] = useConfirmacao();
    const [salvando, setSalvando] = useState(false);
    const [pareando, setPareando] = useState<CanalDeIntegracao | null>(null);
    const [primeiroQr, setPrimeiroQr] = useState<PareamentoDoCanal | null>(null);

    const abrir = (canal: CanalDeIntegracao | 'novo') => {
        setEmEdicao(canal);
        setForm(canal === 'novo'
            ? NOVO
            : {
                nome: canal.nome, tipo: canal.tipo, provedor: canal.provedor,
                ...(canal.numero ? { numero: canal.numero } : {}),
                ativo: true,
                ...(canal.filaPadraoId ? { filaPadraoId: canal.filaPadraoId } : {}),
                ...(canal.atendentePadraoId ? { atendentePadraoId: canal.atendentePadraoId } : {}),
            });
    };

    /* Fila desativada não vira padrão — mas a que já é padrão continua à vista. */
    const filasDoCanal = (atendimento?.filas ?? []).filter((f) => f.ativa || f.id === form.filaPadraoId);
    const filaEscolhida = atendimento?.filas.find((f) => f.id === form.filaPadraoId);
    /* Com fila, só quem é dela (o banco recusa o resto). Sem fila, a equipe toda. */
    const pessoasDoCanal = filaEscolhida ? filaEscolhida.membros : atendimento?.membros ?? [];
    const pessoaForaDaLista = form.atendentePadraoId && !pessoasDoCanal.some((p) => p.id === form.atendentePadraoId);
    const trocarFilaPadrao = (id: string) => {
        const fila = atendimento?.filas.find((f) => f.id === id);
        const proximo = { ...form };
        delete proximo.filaPadraoId;
        if (id) proximo.filaPadraoId = id;
        /* Pessoa que não é da nova fila sai: o banco a recusaria. */
        if (fila && proximo.atendentePadraoId && !fila.membros.some((m) => m.id === proximo.atendentePadraoId)) {
            delete proximo.atendentePadraoId;
        }
        setForm(proximo);
    };
    const trocarPessoaPadrao = (id: string) => {
        const proximo = { ...form };
        delete proximo.atendentePadraoId;
        if (id) proximo.atendentePadraoId = id;
        setForm(proximo);
    };

    const abrirCredencial = (canal: CanalDeIntegracao) => {
        setConectando(canal);
        /* Sempre vazio: não há o que trazer de volta. */
        setCredenciais({});
    };

    const salvarCanal = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            const salvo = await dados().salvarCanal(emEdicao === 'novo' ? null : emEdicao.id, form);
            aviso({ title: emEdicao === 'novo' ? 'Canal criado' : 'Canal salvo', description: form.nome });
            setEmEdicao(null);
            revalidator.revalidate();

            /* Dar um nome e salvar é o que a pessoa veio fazer; o QR é o passo
               seguinte, e pedi-lo em outro clique só adiaria o mesmo pedido.
               Vale para canal NOVO da UAZAPI: editar um que já parear abriria
               uma sessão nova sem ninguém ter pedido. */
            if (emEdicao === 'novo' && salvo.provedor === 'uazapi' && pareamentoDisponivel) {
                await abrirPareamento(salvo, 'criar');
            }
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    /**
     * Abre o pareamento já com o primeiro código na mão.
     *
     * O pedido é feito ANTES de abrir para o modal não nascer com um quadrado
     * vazio: entre o clique e o QR há uma criação de instância no servidor do
     * provedor, e isso demora alguns segundos.
     */
    const abrirPareamento = async (canal: CanalDeIntegracao, acao: 'criar' | 'conectar') => {
        setPareando(canal);
        setPrimeiroQr(null);
        try {
            setPrimeiroQr(await dados().parearCanal(canal.id, acao));
        } catch (e) {
            aviso({
                title: 'Não foi possível gerar o QR Code',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        }
    };

    const desconectarSessao = async (canal: CanalDeIntegracao) => {
        const ok = await confirmar({
            titulo: `Desconectar ${canal.nome}?`,
            descricao: 'A sessão do WhatsApp é encerrada. Para voltar, alguém precisa ler o QR Code de novo com o celular da clínica.',
            confirmar: 'Desconectar',
        });
        if (!ok) return;
        try {
            await dados().parearCanal(canal.id, 'desconectar');
            aviso({
                title: 'Sessão encerrada',
                description: `${canal.nome} · a instância continua lá, pronta para outro QR`,
            });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível desconectar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const salvarCredencial = async () => {
        if (!conectando) return;
        setSalvando(true);
        try {
            await dados().salvarCredencialDoCanal(conectando.id, credenciais);
            /* E-mail: o servidor testa o login SMTP na hora (17/09/2026). */
            if (conectando.provedor === 'smtp') {
                const teste = await dados().validarCanalDeEmail(conectando.id);
                aviso(teste.status === 'conectado'
                    ? { title: 'E-mail conectado', description: 'O servidor aceitou o login. O envio pelas conversas ainda não sai por este e-mail.' }
                    : { title: 'Credencial guardada, mas o login falhou', description: teste.motivo ?? 'O servidor não aceitou a conexão.', tone: 'warning' });
                setConectando(null);
                setCredenciais({});
                revalidator.revalidate();
                return;
            }
            aviso({
                title: 'Credencial guardada',
                description: indisponibilidadeDoProvedor(conectando.provedor)
                    ? 'Cifrada no cofre. A integração com este provedor ainda não existe: o canal não envia nem recebe.'
                    : 'Cifrada no cofre. O canal fica "conectando" até o provedor confirmar.',
            });
            setConectando(null);
            setCredenciais({});
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível guardar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const desconectar = async (canal: CanalDeIntegracao) => {
        const ok = await confirmar({
            titulo: `Remover a credencial de ${canal.nome}?`,
            descricao: 'O canal volta a desconectado e para de enviar até alguém colar a credencial de novo.',
            confirmar: 'Remover credencial',
        });
        if (!ok) return;
        try {
            await dados().removerCredencialDoCanal(canal.id);
            aviso({ title: 'Credencial removida', description: `${canal.nome} · o canal voltou a desconectado` });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<CanalDeIntegracao>[] = [
        {
            key: 'nome', header: 'Canal',
            render: (c) => {
                const p = provedorDeCanal(c.provedor);
                return (
                    <span>
                        <strong style={{ display: 'block', fontWeight: 600 }}>{c.nome}</strong>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                            {p?.nome ?? c.provedor}
                            {c.numero ? ` · ${c.numero}` : ''}
                            {c.instanciaExternaId ? ` · ${c.instanciaExternaId}` : ''}
                        </span>
                    </span>
                );
            },
        },
        {
            key: 'status', header: 'Situação', width: 250,
            render: (c) => {
                const estado = estadoDoCanal(c);
                return (
                    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                        <Badge tone={estado.tom} dot>{estado.rotulo}</Badge>
                        {c.ultimoErro && <span style={{ fontSize: 11.5, color: 'var(--danger)' }}>{c.ultimoErro}</span>}
                    </span>
                );
            },
        },
        {
            key: 'acoes', header: '', width: 320, align: 'right',
            render: (c) => {
                /* Na UAZAPI a conexão é um QR; nos outros provedores é um
                   formulário de credencial. Mesmo lugar, ações diferentes,
                   porque o que a pessoa faz a seguir é diferente. */
                const porQr = c.provedor === 'uazapi' && pareamentoDisponivel;
                return (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        {porQr ? (
                            <>
                                {c.status !== 'conectado' && (
                                    <Button
                                        size="sm" variant="secondary"
                                        iconLeft={<Icon name="qr-code" size={14} />}
                                        onClick={() => abrirPareamento(c, c.temCredencial ? 'conectar' : 'criar')}
                                    >
                                        Conectar
                                    </Button>
                                )}
                                {c.temCredencial && (
                                    <Button size="sm" variant="ghost" onClick={() => desconectarSessao(c)}>
                                        Desconectar
                                    </Button>
                                )}
                            </>
                        ) : (
                            <>
                                <Button size="sm" variant="secondary" onClick={() => abrirCredencial(c)}>
                                    {c.temCredencial ? 'Trocar credencial' : 'Conectar'}
                                </Button>
                                {c.temCredencial && (
                                    <Button size="sm" variant="ghost" onClick={() => desconectar(c)}>Desconectar</Button>
                                )}
                            </>
                        )}
                        <IconButton
                            icon={<Icon name="pencil" size={14} />} size="sm"
                            label={`Editar ${c.nome}`} onClick={() => abrir(c)}
                        />
                    </span>
                );
            },
        },
    ];

    const provedor = conectando ? provedorDeCanal(conectando.provedor) : undefined;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* A promessa que esta tela faz, e que o banco cumpre. */}
            <Alert tone="info" dismissible={false} title="O que você digitar aqui não volta">
                Token e senha entram por uma função do banco e ficam <strong>cifrados no cofre</strong>. Nem
                esta tela nem nenhuma consulta do aplicativo conseguem lê-los de novo — por isso o formulário
                aparece vazio mesmo num canal já conectado. Para trocar, digite de novo.
            </Alert>

            {!pareamentoDisponivel && canais.some((c) => c.provedor === 'uazapi') && (
                <Alert tone="warning" dismissible={false} title="O pareamento por QR Code está indisponível">
                    A VecchIA ainda não ligou um servidor de WhatsApp. Até lá, um canal da UAZAPI só conecta
                    colando instância e token criados por fora.
                </Alert>
            )}

            <Card
                title="Canais de mensagem"
                subtitle="É por eles que a Comunicação envia e recebe, e é o que as automações esperam"
                padding={0}
                actions={(
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('novo')}>
                        Novo canal
                    </Button>
                )}
            >
                <DataTable
                    columns={colunas} rows={canais} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhum canal cadastrado."
                />
            </Card>

            <Card title="Gateways de pagamento" padding={18}>
                <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                    Os gateways ficam em{' '}
                    <Link to="/app/financeiro/maquininha" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                        Financeiro › Gateways de pagamento
                    </Link>
                    , junto das taxas e da conciliação que dependem deles.
                </p>
            </Card>

            {anuncios && (
                <CartaoDaMeta
                    disponivel={anuncios.disponivel} conta={anuncios.conta} podeGerir={anuncios.podeGerir}
                    escolherAgora={voltaDaMeta === 'escolher'}
                    aoMudar={() => {
                        setParams((p) => { const n = new URLSearchParams(p); n.delete('meta-ads'); return n; }, { replace: true });
                        void revalidator.revalidate();
                    }}
                />
            )}

            {/* A conexão é de cada profissional, com a conta Google dele (0152): não há o que
                configurar pela clínica, só dizer onde fica. */}
            <Card title="Google Agenda" padding={18}>
                <p style={{ margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                    Cada profissional conecta a própria conta Google em Editar conta, no menu com o nome dele no topo da tela.
                    Os atendimentos e bloqueios dele vão para a agenda Google; o que muda lá não volta para a VecchIA.
                </p>
            </Card>

            {confirmacao}

            {/* A lista do que NÃO existe, num lugar só. Cada uma depende de conta
                num serviço de fora que a Fórmula MED ainda não tem — e uma tela que
                sugerisse "conectar" qualquer uma delas estaria prometendo. */}
            <Card title="Ainda sem integração" subtitle="Dependem de conta num serviço externo que a Fórmula MED ainda não tem" padding={18}>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' }}>
                    <li><strong>Nota fiscal de serviço (NFS-e)</strong>: fica em Financeiro › Notas fiscais. Com os dados fiscais e o certificado, use "Ligar ao emissor" (Focus NFe) e as notas saem dali, no padrão da sua prefeitura.</li>
                    <li><strong>Assinatura digital</strong>: o documento é emitido e a assinatura colhida é registrada à mão.</li>
                    <li><strong>Cobrança por boleto, PIX ou cartão</strong> e <strong>conciliação por arquivo do banco (CNAB)</strong>: a baixa é registrada à mão.</li>
                    <li><strong>WhatsApp oficial (API da Meta) e Instagram Direct</strong>: a credencial pode ser guardada, mas o canal não envia nem recebe.</li>
                    <li><strong>E-mail da clínica</strong>: o login no servidor SMTP é testado e o canal conecta, mas as conversas ainda não enviam nem recebem por ele.</li>
                    <li><strong>SMS</strong> e <strong>chat do site</strong>: não há canal desses tipos.</li>
                    <li><strong>Google Ads e Google Analytics</strong>: ainda não são lidos; a Meta Ads, sim, no cartão acima.</li>
                </ul>
            </Card>

            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'novo' ? 'Novo canal' : 'Editar canal'}
                onClose={() => setEmEdicao(null)}
                dirty={canalSujo}
                width={560}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvarCanal} disabled={!form.nome.trim() || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(210px,100%),1fr))', gap: 14 }}>
                    <FormField label="Nome" htmlFor="canal-nome" hint="Como a equipe chama este canal." required style={{ gridColumn: '1 / -1' }}>
                        <Input id="canal-nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} placeholder="Canal 01 · Recepção" />
                    </FormField>
                    <FormField label="Provedor" htmlFor="canal-provedor" style={{ gridColumn: '1 / -1' }}>
                        <Select
                            id="canal-provedor" value={form.provedor}
                            onChange={(v) => {
                                const p = provedorDeCanal(v);
                                setForm({ ...form, provedor: v, ...(p ? { tipo: p.tipo } : {}) });
                            }}
                            options={PROVEDORES_DE_CANAL.map((p) => ({ value: p.id, label: p.nome }))}
                        />
                    </FormField>
                    {provedorDeCanal(form.provedor) && (
                        <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                            {provedorDeCanal(form.provedor)?.descricao}
                            <br />
                            <strong>Antes de conectar:</strong> {provedorDeCanal(form.provedor)?.prerequisito}
                        </p>
                    )}
                    <FormField label="Número ou identificação" htmlFor="canal-numero">
                        <Input
                            id="canal-numero" value={form.numero ?? ''}
                            onChange={(v) => setForm({ ...form, numero: v || undefined })}
                            placeholder="5511999999999"
                        />
                    </FormField>
                    {atendimento && (
                        <fieldset style={{ gridColumn: '1 / -1', border: 0, margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(210px,100%),1fr))', gap: 14 }}>
                            <legend style={{ marginBottom: 4, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                                Para onde vai a conversa nova
                            </legend>
                            <FormField
                                label="Fila padrão" htmlFor="canal-fila"
                                hint={filaEscolhida ? `Distribuição: ${rotuloDaDistribuicao(filaEscolhida.distribuicao)}.` : 'Sem fila, a conversa fica em Pendentes para a equipe toda.'}
                            >
                                <Select
                                    id="canal-fila" value={form.filaPadraoId ?? ''} onChange={trocarFilaPadrao}
                                    options={[
                                        { value: '', label: 'Nenhuma' },
                                        ...filasDoCanal.map((f) => ({ value: f.id, label: f.ativa ? f.nome : `${f.nome} (desativada)` })),
                                    ]}
                                />
                            </FormField>
                            <FormField
                                label="Usuário padrão" htmlFor="canal-pessoa"
                                hint={filaEscolhida
                                    ? 'Recebe a conversa quando está online; fora do ar, a fila distribui.'
                                    : 'A conversa fica reservada para esta pessoa em Pendentes.'}
                            >
                                <Select
                                    id="canal-pessoa" value={form.atendentePadraoId ?? ''} onChange={trocarPessoaPadrao}
                                    options={[
                                        { value: '', label: 'Nenhum' },
                                        ...pessoasDoCanal.map((p) => ({ value: p.id, label: p.nome })),
                                        ...(pessoaForaDaLista && form.atendentePadraoId ? [{ value: form.atendentePadraoId, label: 'Pessoa que não está mais disponível' }] : []),
                                    ]}
                                />
                            </FormField>
                        </fieldset>
                    )}
                    <Switch checked={form.ativo} onChange={(v) => setForm({ ...form, ativo: v })} label="Ativo" />
                </div>
            </Modal>

            <PareamentoModal
                canal={pareando}
                inicial={primeiroQr}
                onFechar={() => { setPareando(null); setPrimeiroQr(null); revalidator.revalidate(); }}
                onPareado={() => revalidator.revalidate()}
            />

            <Modal
                open={conectando !== null}
                title={conectando ? `Conectar ${conectando.nome}` : 'Conectar'}
                onClose={() => setConectando(null)}
                dirty={credencialSuja}
                width={560}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setConectando(null)}>Cancelar</Button>
                        <Button
                            onClick={salvarCredencial}
                            disabled={salvando || !provedor?.campos.some((c) => credenciais[c.chave]?.trim())}
                        >
                            {salvando ? 'Guardando…' : 'Guardar credencial'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {provedor ? (
                        <>
                            <p style={{ margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' }}>
                                {provedor.prerequisito}{' '}
                                <a href={provedor.documentacao} target="_blank" rel="noreferrer" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                                    Documentação do provedor
                                </a>
                            </p>

                            {conectando?.temCredencial && (
                                <Alert tone="warning" dismissible={false}>
                                    Já existe credencial guardada
                                    {conectando.credencialRotacionadaEm || conectando.credencialEm
                                        ? ` desde ${new Date(conectando.credencialRotacionadaEm ?? conectando.credencialEm ?? '').toLocaleDateString('pt-BR')}`
                                        : ''}
                                    . Guardar de novo <strong>substitui</strong> a que está lá.
                                </Alert>
                            )}

                            {provedor.campos.map((campo) => (
                                <FormField
                                    key={campo.chave} label={campo.rotulo} htmlFor={`cred-${campo.chave}`}
                                    hint={campo.ajuda} required={!campo.opcional}
                                >
                                    <Input
                                        id={`cred-${campo.chave}`}
                                        type={campo.tipo === 'senha' ? 'password' : 'text'}
                                        autoComplete="off"
                                        value={credenciais[campo.chave] ?? ''}
                                        onChange={(v) => setCredenciais({ ...credenciais, [campo.chave]: v })}
                                    />
                                </FormField>
                            ))}
                        </>
                    ) : (
                        <Alert tone="danger" dismissible={false} title="Provedor desconhecido">
                            Este canal aponta para um provedor que a aplicação não conhece. Edite o canal e
                            escolha um da lista.
                        </Alert>
                    )}
                </div>
            </Modal>
        </div>
    );
}
